// src/components/landing/QuestoesBlock.tsx
// Bloco 04 — Banco de Questões | GRAIA Landing
// Prefixo CSS: qstb-

import React, { useState, useEffect, useRef } from 'react';
import { Filter, BookOpen, Clock, BarChart2, Layers, History } from 'lucide-react';
import './QuestoesBlock.css';

// ─────────────────────────────────────────────
// TIPOS
// ─────────────────────────────────────────────

interface Tema {
    count: number;
    subtopics: string[];
}

interface Sistema {
    count: number;
    themes: Record<string, Tema>;
}

interface Ciclo {
    count: number;
    systems: Record<string, Sistema>;
}

type HierarchyType = Record<string, Ciclo>;

// ─────────────────────────────────────────────
// HIERARQUIA REAL DE FILTROS
// Ciclo → Sistema → Tema → Subtema
// ─────────────────────────────────────────────

const HIERARCHY: HierarchyType = {
    'Ciclo Básico': {
        count: 1840,
        systems: {
            'Sistema Nervoso': {
                count: 620,
                themes: {
                    'Histologia do Córtex': { count: 142, subtopics: ['Camadas corticais', 'Neurônios piramidais', 'Células gliais'] },
                    'Potencial de Ação': { count: 98, subtopics: ['Canais iônicos', 'Período refratário', 'Condução saltatória'] },
                    'Sinapses Químicas': { count: 87, subtopics: ['Vesículas sinápticas', 'Receptores pós-sinápticos', 'Recaptação'] },
                },
            },
            'Sistema Cardiovascular': {
                count: 510,
                themes: {
                    'Anatomia do Coração': { count: 175, subtopics: ['Câmaras cardíacas', 'Valvas', 'Irrigação coronariana'] },
                    'ECG': { count: 134, subtopics: ['Ondas P, QRS, T', 'Eixo elétrico', 'Bloqueios de ramo'] },
                    'Ciclo Cardíaco': { count: 88, subtopics: ['Sístole ventricular', 'Diástole', 'Sons cardíacos'] },
                },
            },
            'Sistema Endócrino': {
                count: 380,
                themes: {
                    'Tireoide': { count: 120, subtopics: ['T3 e T4', 'TSH', 'Regulação hipotalâmica'] },
                    'Pâncreas': { count: 108, subtopics: ['Insulina', 'Glucagon', 'Células beta'] },
                    'Suprarrenal': { count: 95, subtopics: ['Cortisol', 'Aldosterona', 'Catecolaminas'] },
                },
            },
        },
    },
    'Ciclo Clínico': {
        count: 1530,
        systems: {
            'Clínica Médica': {
                count: 570,
                themes: {
                    'Cardiologia Clínica': { count: 195, subtopics: ['Insuficiência cardíaca', 'Hipertensão arterial', 'Síndrome coronariana'] },
                    'Pneumologia': { count: 160, subtopics: ['Asma', 'DPOC', 'Pneumonia adquirida'] },
                    'Nefrologia': { count: 110, subtopics: ['IRA', 'Síndrome nefrótica', 'Glomerulopatias'] },
                },
            },
            'Cirurgia Geral': {
                count: 445,
                themes: {
                    'Abdome Agudo': { count: 155, subtopics: ['Apendicite', 'Colecistite', 'Perfuração gástrica'] },
                    'Trauma': { count: 140, subtopics: ['ATLS', 'Pneumotórax', 'Hemotórax'] },
                    'Pós-operatório': { count: 88, subtopics: ['Complicações precoces', 'Infecção de sítio', 'Deiscência'] },
                },
            },
            'Ginecologia': {
                count: 310,
                themes: {
                    'Ciclo Menstrual': { count: 98, subtopics: ['FSH e LH', 'Ovulação', 'Fase lútea'] },
                    'Patologia Cervical': { count: 115, subtopics: ['NIC', 'Colposcopia', 'HPV'] },
                    'Contracepção': { count: 72, subtopics: ['Contraceptivos orais', 'DIU', 'Métodos de barreira'] },
                },
            },
        },
    },
    'Internato': {
        count: 980,
        systems: {
            'Urgência e Emergência': {
                count: 390,
                themes: {
                    'PCR e Ressuscitação': { count: 138, subtopics: ['BLS', 'ACLS', 'Desfibrilação'] },
                    'Choque': { count: 122, subtopics: ['Hipovolêmico', 'Distributivo', 'Cardiogênico'] },
                    'Intoxicações': { count: 85, subtopics: ['Organofosforados', 'Paracetamol', 'Benzodiazepínicos'] },
                },
            },
            'Pediatria': {
                count: 320,
                themes: {
                    'Neonatologia': { count: 115, subtopics: ['Reanimação neonatal', 'Sepse neonatal', 'Icterícia'] },
                    'Imunizações': { count: 98, subtopics: ['Calendário vacinal', 'Contraindicações', 'ESAVI'] },
                    'Crescimento': { count: 72, subtopics: ['Curvas de crescimento', 'Baixa estatura', 'Puberdade'] },
                },
            },
            'Saúde Coletiva': {
                count: 185,
                themes: {
                    'Vigilância Epidemiológica': { count: 72, subtopics: ['Notificação compulsória', 'Surtos', 'Investigação'] },
                    'Atenção Primária': { count: 65, subtopics: ['ESF', 'NASF', 'Adscrição de território'] },
                    'SUS': { count: 48, subtopics: ['Princípios e diretrizes', 'Financiamento', 'Controle social'] },
                },
            },
        },
    },
};

// ─────────────────────────────────────────────
// DADOS ESTÁTICOS
// ─────────────────────────────────────────────

const PILLARS = [
    {
        icon: Layers,
        title: 'Arquitetura hierárquica de conteúdo',
        text: 'Do ciclo ao subtópico específico. Você navega com precisão e monta o treino que realmente precisa.',
    },
    {
        icon: Clock,
        title: 'Simulados e provas oficiais',
        text: 'Monte simulados sob medida ou responda provas completas de exames oficiais.',
    },
    {
        icon: History,
        title: 'Histórico e reexecução estratégica',
        text: 'Cada tentativa fica registrada com nota, tempo e padrão de erro. Refaça e compare sua evolução.',
    },
    {
        icon: BarChart2,
        title: 'Monitoramento inteligente de desempenho',
        text: 'Cada tentativa é registrada com nota, tempo e padrão de acertos. Visualize sua evolução com dados claros.',
    },
];

// ─────────────────────────────────────────────
// DEMO: FILTRO HIERÁRQUICO REAL
// ─────────────────────────────────────────────

const FilterDemo: React.FC<{ active: boolean }> = ({ active }) => {
    const [ciclo, setCiclo] = useState('');
    const [sistema, setSistema] = useState('');
    const [tema, setTema] = useState('');
    const [subtopico, setSubtopico] = useState('');
    const [displayCount, setDisplayCount] = useState(0);
    const [countFade, setCountFade] = useState(false);

    const animateCount = (target: number) => {
        setCountFade(true);
        setTimeout(() => {
            setDisplayCount(target);
            setCountFade(false);
        }, 160);
    };

    const handleCiclo = (val: string) => {
        setCiclo(val); setSistema(''); setTema(''); setSubtopico('');
        if (val) animateCount(HIERARCHY[val]?.count ?? 0);
        else animateCount(0);
    };

    const handleSistema = (val: string) => {
        setSistema(val); setTema(''); setSubtopico('');
        if (val && ciclo) {
            const s = HIERARCHY[ciclo]?.systems[val];
            animateCount(s?.count ?? 0);
        }
    };

    const handleTema = (val: string) => {
        setTema(val); setSubtopico('');
        if (val && ciclo && sistema) {
            const t = HIERARCHY[ciclo]?.systems[sistema]?.themes[val];
            animateCount(t?.count ?? 0);
        }
    };

    const handleSubtopico = (val: string) => {
        setSubtopico(val);
        if (val && ciclo && sistema && tema) {
            // subtópicos não têm count próprio — mostra count do tema / número de subtópicos
            const t = HIERARCHY[ciclo]?.systems[sistema]?.themes[tema];
            const base = t?.count ?? 0;
            const subs = t?.subtopics?.length ?? 1;
            animateCount(Math.round(base / subs));
        }
    };

    // Sequência automática quando ativo
    useEffect(() => {
        if (!active) {
            setCiclo(''); setSistema(''); setTema(''); setSubtopico(''); setDisplayCount(0);
            return;
        }
        const steps = [
            { delay: 400, fn: () => handleCiclo('Ciclo Básico') },
            { delay: 1300, fn: () => handleSistema('Sistema Cardiovascular') },
            { delay: 2200, fn: () => handleTema('ECG') },
            { delay: 3100, fn: () => handleSubtopico('Ondas P, QRS, T') },
        ];
        const timers = steps.map(({ delay, fn }) => setTimeout(fn, delay));
        return () => timers.forEach(clearTimeout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active]);

    const ciclos = Object.keys(HIERARCHY);
    const sistemas = ciclo ? Object.keys(HIERARCHY[ciclo]?.systems ?? {}) : [];
    const temas = sistema && ciclo ? Object.keys(HIERARCHY[ciclo]?.systems[sistema]?.themes ?? {}) : [];
    const subtopicos = tema && sistema && ciclo
        ? HIERARCHY[ciclo]?.systems[sistema]?.themes[tema]?.subtopics ?? []
        : [];

    const levels = [
        { label: 'Ciclo', value: ciclo, options: ciclos, onChange: handleCiclo, locked: false },
        { label: 'Sistema', value: sistema, options: sistemas, onChange: handleSistema, locked: !ciclo },
        { label: 'Tema', value: tema, options: temas, onChange: handleTema, locked: !sistema },
        { label: 'Subtópico', value: subtopico, options: subtopicos, onChange: handleSubtopico, locked: !tema },
    ];

    return (
        <div className="qstb-demo-panel">
            <div className="qstb-mock-header">
                <span className="qstb-mock-logo">GRAIA</span>
                <span className="qstb-mock-sep" />
                <span className="qstb-mock-subtitle">Filtrar Questões</span>
            </div>

            <div className="qstb-filter-body">
                {levels.map((lvl) => (
                    <div key={lvl.label} className="qstb-filter-row">
                        <label className="qstb-filter-label">{lvl.label}</label>
                        <div className={`qstb-select-wrap ${lvl.locked ? 'qstb-select-wrap--locked' : ''}`}>
                            <select
                                className="qstb-select"
                                disabled={lvl.locked}
                                value={lvl.value}
                                onChange={e => lvl.onChange(e.target.value)}
                            >
                                <option value="">Selecionar...</option>
                                {lvl.options.map((opt: string) => (
                                    <option key={opt} value={opt}>{opt}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                ))}

                <div className="qstb-filter-footer">
                    <span className={`qstb-count ${countFade ? 'qstb-count--transitioning' : ''}`}>
                        {displayCount > 0
                            ? <><strong>{displayCount.toLocaleString('pt-BR')}</strong> questões</>
                            : <span className="qstb-count-placeholder">Selecione um ciclo</span>
                        }
                    </span>
                    <button className="qstb-btn-practice" disabled={!ciclo}>
                        Praticar
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────
// DEMO: QUESTÃO
// ─────────────────────────────────────────────

const QuestionDemo: React.FC<{ active: boolean }> = ({ active }) => {
    const [answered, setAnswered] = useState(false);
    const [selected, setSelected] = useState<string | null>(null);
    const [showResolution, setShowResolution] = useState(false);

    useEffect(() => {
        if (!active) { setAnswered(false); setSelected(null); setShowResolution(false); return; }
        const t1 = setTimeout(() => setSelected('C'), 600);
        const t2 = setTimeout(() => setAnswered(true), 1100);
        const t3 = setTimeout(() => setShowResolution(true), 1400);
        return () => [t1, t2, t3].forEach(clearTimeout);
    }, [active]);

    const options = [
        { key: 'A', text: 'Córtex pré-frontal dorsolateral', correct: false },
        { key: 'B', text: 'Hipocampo', correct: false },
        { key: 'C', text: 'Área de Broca', correct: true },
        { key: 'D', text: 'Lóbulo parietal inferior', correct: false },
    ];

    return (
        <div className="qstb-demo-panel">
            <div className="qstb-mock-header">
                <span className="qstb-mock-logo">GRAIA</span>
                <span className="qstb-mock-sep" />
                <span className="qstb-mock-subtitle">Banco de Questões</span>
                <span className="qstb-mock-tag">Neurologia · Difícil</span>
            </div>

            <div className="qstb-question-body">
                <p className="qstb-question-text">
                    Qual estrutura cortical é classicamente associada à produção da linguagem falada?
                </p>

                <div className="qstb-options">
                    {options.map(opt => (
                        <button
                            key={opt.key}
                            className={[
                                'qstb-option',
                                selected === opt.key && !answered ? 'qstb-option--selected' : '',
                                answered && opt.correct ? 'qstb-option--correct' : '',
                                answered && selected === opt.key && !opt.correct ? 'qstb-option--wrong' : '',
                            ].join(' ')}
                            onClick={() => !answered && setSelected(opt.key)}
                            disabled={answered}
                        >
                            <span className="qstb-option-key">{opt.key}</span>
                            <span className="qstb-option-text">{opt.text}</span>
                        </button>
                    ))}
                </div>

                {!answered && (
                    <button
                        className="qstb-btn-answer"
                        disabled={!selected}
                        onClick={() => { setAnswered(true); setTimeout(() => setShowResolution(true), 300); }}
                    >
                        Responder
                    </button>
                )}

                {showResolution && (
                    <div className="qstb-resolution">
                        <p className="qstb-resolution-title">Resolução</p>
                        <p className="qstb-resolution-text">
                            A Área de Broca, localizada no giro frontal inferior do hemisfério dominante,
                            é responsável pela produção da linguagem falada. Lesões nessa região causam
                            afasia de Broca (expressiva).
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────
// COMPONENTE PRINCIPAL
// ─────────────────────────────────────────────

const QuestoesBlock: React.FC = () => {
    const sectionRef = useRef<HTMLElement>(null);
    const [visible, setVisible] = useState(false);
    const [activeTab, setActiveTab] = useState<0 | 1>(0);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.12 }
        );
        if (sectionRef.current) observer.observe(sectionRef.current);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (!visible) return;
        const t = setTimeout(() => setActiveTab(1), 5000);
        return () => clearTimeout(t);
    }, [visible]);

    const TABS = [
        { label: 'Filtrar', icon: Filter },
        { label: 'Questão', icon: BookOpen },
    ];

    return (
        <section
            className={`qstb-section ${visible ? 'qstb-section--visible' : ''}`}
            ref={sectionRef}
        >
            <div className="qstb-container">

                {/* ── CABEÇALHO ── */}
                <div className="qstb-header">
                    <div className="qstb-header-top">
                        <p className="qstb-label">Banco de Questões</p>
                        {/*<div className="qstb-coming-soon-bar">
                            <Construction size={13} />
                            <span>Em breve</span>
                        </div> */}
                    </div>
                    <h2 className="qstb-headline">
                        Você decide exatamente<br />o que treinar.
                    </h2>
                    <p className="qstb-subheadline">
                        Do macro ao micro. Com controle total de volume e desempenho.
                    </p>
                </div>

                {/* ── GRID ── */}
                <div className="qstb-grid">

                    {/* ─── COLUNA ESQUERDA: DEMO ─── */}
                    <div className="qstb-demo-col">
                        <div className="qstb-tabs">
                            {TABS.map((tab, i) => {
                                const Icon = tab.icon;
                                return (
                                    <button
                                        key={tab.label}
                                        className={`qstb-tab ${activeTab === i ? 'qstb-tab--active' : ''}`}
                                        onClick={() => setActiveTab(i as 0 | 1)}
                                    >
                                        <Icon size={13} strokeWidth={1.75} />
                                        {tab.label}
                                    </button>
                                );
                            })}
                        </div>

                        <div className="qstb-mock-shell">
                            <div className={`qstb-slide ${activeTab === 0 ? 'qstb-slide--active' : ''}`}>
                                <FilterDemo active={visible && activeTab === 0} />
                            </div>
                            <div className={`qstb-slide ${activeTab === 1 ? 'qstb-slide--active' : ''}`}>
                                <QuestionDemo active={visible && activeTab === 1} />
                            </div>
                        </div>
                    </div>

                    {/* ─── COLUNA DIREITA: PILARES ─── */}
                    <div className="qstb-pillars-col">
                        {PILLARS.map((pillar, i) => {
                            const Icon = pillar.icon;
                            return (
                                <div
                                    key={pillar.title}
                                    className="qstb-pillar"
                                >
                                    <div className="qstb-pillar-accent" />
                                    <div className="qstb-pillar-icon">
                                        <Icon size={15} strokeWidth={1.75} />
                                    </div>
                                    <div className="qstb-pillar-body">
                                        <h3 className="qstb-pillar-title">{pillar.title}</h3>
                                        <p className="qstb-pillar-text">{pillar.text}</p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                </div>
            </div>
        </section>
    );
};

export default QuestoesBlock;